import { Box, Button, Chip, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { useEffect, useState } from 'react';
import CustomerCareDTable from '../../../Shared/CustomerCareDTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { parseDateToLocal } from '../../../../shared/utils';
import {
  getCareteamRoleColor,
  getFacilityRoleColor,
} from '../../../../shared/roles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import {
  faClock as faClockSolid,
  faCircleXmark as faCircleXmarkSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { GridColDef } from '@mui/x-data-grid';
import {
  clearHubActionStatus,
  createCareteamInvite,
  deleteCareteamInvite,
  deleteOwnerContact,
  fetchOwnerContacts,
  resendCareteamInvite,
  updateCareteamRole,
} from '../../../../store/hubOwner/actionCreators';
import NoTabDataCard from '../../../Hubs/HubDetails/tabs/components/NoTabDataCard';
import LoadingSpinner from '../../../Shared/LoadingSpinner';
import EditRoleModal from './EditRoleModal';
import InviteNewUserModal from './InviteNewUserModal';
import {
  resetSuccessStates,
  setEditStaffStatus,
  updateCaregiverById,
  updateStaffDetails,
} from '../../../../store/facility/actionCreators';
import { HubApiActions } from '../../../../store/hubOwner/types';
import {
  lightBlack,
  lightGrey,
  lightPurple,
  orange,
  outlineSeparator,
} from '../../../../mainTheme';
import NewModal from '../../../Shared/NewModal';
import DeleteCareTeamUserModal from './DeleteCareTeamUserModal';
import { StatusMessage } from '../../../Hubs/HubDetails/AssignHubController/components/StatusMessage';
import { ReactComponent as DeleteSuccessIcon } from '../../../../images/icons/delete_success_icon.svg';
import { ReactComponent as DeleteFailedIcon } from '../../../../images/icons/delete_failed_icon.svg';
import { ReactComponent as InviteSuccessIcon } from '../../../../images/icons/add_careteam_success_icon.svg';
import { ReactComponent as InviteFailedIcon } from '../../../../images/icons/add_careteam_failed_icon.svg';
import {
  API_ACTION_STATUS,
  FACILITY_ROLE,
  PRIVILEGE,
} from '../../../../shared/constants';

const isInvite = (row: any) => row.invite_status_id != null;
const isPendingInvite = (row: any) =>
  isInvite(row) && row.status === 'invite_pending';

const FACILITY_ROLE_API_ENUM: Record<string, string> = {
  facility_administrator: 'facility_admin',
  location_administrator: 'location_admin',
  caregiver: 'caregiver',
};
const FACILITY_ROLE_MAP: Record<string, number> = {
  facility_administrator: FACILITY_ROLE.FACILITY_ADMIN,
  location_administrator: FACILITY_ROLE.FACILITY_ADMIN,
  caregiver: FACILITY_ROLE.FACILITY_CAREGIVER,
};

interface CareTeamTabProps {
  ownerId: number;
  facility_id: number;
  isFacilityAdmin: boolean;
  ownerEmail?: string;
}

const CareTeamTab: React.FC<CareTeamTabProps> = ({
  ownerId,
  facility_id,
  isFacilityAdmin,
  ownerEmail,
}) => {
  const dispatch = useDispatch();

  const {
    ownerContacts,
    ownerContactsLoading,
    actionStatus,
    careteamRoleLoading,
    deleteContactLoading,
  } = useSelector((state: RootState) => state.hubOwner);

  const {
    editStaffStatus,
    isLoading: facilityLoading,
    updateCaregiverSuccess,
  } = useSelector((state: RootState) => state.facility);
  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );

  const canManageInvites =
    dashboardUserInfo?.privilege_id === PRIVILEGE.CUSTOMER_CARE ||
    dashboardUserInfo?.facility_role_id === FACILITY_ROLE.FACILITY_ADMIN ||
    dashboardUserInfo?.facility_role_id === FACILITY_ROLE.FACILITY_SCHEDULER;
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteStatus, setShowDeleteStatus] = useState<
    (typeof API_ACTION_STATUS)[keyof typeof API_ACTION_STATUS] | null
  >(null);
  const [showInviteStatus, setShowInviteStatus] = useState<
    (typeof API_ACTION_STATUS)[keyof typeof API_ACTION_STATUS] | null
  >(null);

  useEffect(() => {
    if (ownerId) {
      dispatch(fetchOwnerContacts(ownerId));
    }
  }, [dispatch, ownerId]);

  const filteredContacts = ownerContacts || [];

  // Ensure ownerContacts had unique IDs since some might not have customer_id
  const rows = filteredContacts.map((row, idx) => ({
    ...row,
    id: row.customer_id ?? `temp-${idx}`,
  }));

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Caregiver',
      flex: 1,
      valueGetter: (params: any) =>
        `${params.row.first_name} ${params.row.last_name}`,
    },
    {
      field: 'careteam_role_id',
      headerName: 'Role',
      flex: 1,
      sortable: true,
      renderCell: (params: any) => {
        // Facility staff on the careteam are colored by facility role; everyone
        // else by careteam role — mirroring how the API picks the label.
        const colorStyles = params.row.facility_role_id
          ? getFacilityRoleColor(params.row.facility_role_id)
          : getCareteamRoleColor(params.value);
        const roleLabel = params?.row?.careteam_role;
        const isPending = isPendingInvite(params.row);
        const showStatusBadge = isInvite(params.row);
        return (
          <Chip
            label={
              showStatusBadge ? (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <span style={{ color: lightGrey, marginRight: 4 }}>
                    {roleLabel}
                  </span>
                  <FontAwesomeIcon
                    icon={isPending ? faClockSolid : faCircleXmarkSolid}
                    style={{ color: orange, width: 14, height: 14 }}
                  />
                  <span style={{ color: orange }}>
                    {isPending ? 'Pending' : 'Invite expired'}
                  </span>
                </Box>
              ) : (
                roleLabel
              )
            }
            sx={{
              backgroundColor: colorStyles.backgroundColor,
              color: lightBlack,
              borderRadius: '99px',
              p: 1,
            }}
            size="small"
          />
        );
      },
    },
    {
      field: 'last_login_at',
      headerName: 'Last login',
      flex: 1,
      renderCell: (params: any) => {
        if (isInvite(params.row)) {
          return (
            <span
              style={{ color: lightPurple, fontWeight: 500, cursor: 'pointer' }}
              onClick={() => {
                dispatch(
                  resendCareteamInvite(ownerId, {
                    first_name: params.row.first_name,
                    last_name: params.row.last_name,
                    email: params.row.email,
                    careteam_role_id: params.row.careteam_role_id,
                  }),
                );
              }}
            >
              Resend invite
            </span>
          );
        }
        return params.value
          ? parseDateToLocal(params.value, 'EEE MMM dd yyyy')
          : 'N/A';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params: any) => {
        const isRowInvite = isInvite(params.row);

        if (isRowInvite && !canManageInvites) {
          return null;
        }

        if (isRowInvite) {
          return (
            <Box
              display="flex"
              justifyContent="end"
              borderRadius="8px"
              border={`1px solid ${outlineSeparator}`}
            >
              <Tooltip title="Delete invite">
                <Box p={0.5}>
                  <IconButton
                    onClick={() => {
                      dispatch(deleteCareteamInvite(ownerId, params.row.email));
                    }}
                    size="small"
                  >
                    <FontAwesomeIcon
                      icon={faTrashCan}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                      size="sm"
                    />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          );
        }

        return (
          <Box
            display="flex"
            justifyContent="end"
            borderRadius="8px"
            border={`1px solid ${outlineSeparator}`}
          >
            <Tooltip title="Edit">
              <Box p={0.5}>
                <IconButton
                  onClick={() => {
                    setSelectedContact(params.row);
                  }}
                  size="small"
                >
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    size="sm"
                    style={{ width: 16, height: 16, flexShrink: 0 }}
                  />
                </IconButton>
              </Box>
            </Tooltip>
            <Box width="1px" bgcolor={outlineSeparator} />
            <Tooltip title="Delete">
              <Box p={0.5}>
                <IconButton
                  onClick={() => {
                    setSelectedContact(params.row);
                    setShowDeleteModal(true);
                  }}
                  size="small"
                >
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    style={{ width: 16, height: 16, flexShrink: 0 }}
                    size="sm"
                  />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleSaveRole = (newRole: string | number) => {
    if (selectedContact?.facility_role_id) {
      if (isFacilityAdmin) {
        dispatch(
          updateCaregiverById(selectedContact.customer_id, {
            facility_role_id: FACILITY_ROLE_MAP[newRole as string],
          }),
        );
      } else {
        // Map UI role to API enum
        const apiRole = FACILITY_ROLE_API_ENUM[newRole as string];
        dispatch(
          updateStaffDetails(
            facility_id,
            selectedContact.customer_id,
            {
              new_role: apiRole ?? '',
            },
            true, // show toast
          ),
        );
      }
    } else {
      // Careteam role update
      dispatch(
        updateCareteamRole(ownerId, selectedContact.customer_id, {
          new_careteam_role_id: newRole as number,
        }),
      );
    }
  };

  useEffect(() => {
    if (editStaffStatus === true) {
      setSelectedContact(null);
      dispatch(setEditStaffStatus(null)); // clear status
      dispatch(fetchOwnerContacts(ownerId));
    }
  }, [editStaffStatus, dispatch]);

  useEffect(() => {
    if (updateCaregiverSuccess) {
      setSelectedContact(null);
      dispatch(resetSuccessStates());
      dispatch(fetchOwnerContacts(ownerId));
    }
  }, [updateCaregiverSuccess, dispatch, ownerId]);

  useEffect(() => {
    if (
      actionStatus?.apiAction === HubApiActions.UPDATE_CARETEAM_ROLE &&
      actionStatus.success
    ) {
      setSelectedContact(null);
      dispatch(clearHubActionStatus());
      dispatch(fetchOwnerContacts(ownerId));
    }
  }, [actionStatus, dispatch]);

  // This is to control the delete action status UI
  useEffect(() => {
    if (actionStatus?.apiAction === HubApiActions.DELETE_OWNER_CONTACT) {
      if (actionStatus.success) {
        setShowDeleteStatus(API_ACTION_STATUS.SUCCESS);
        setShowDeleteModal(false);
        setSelectedContact(null);
      } else if (actionStatus.success === false && actionStatus.error) {
        setShowDeleteStatus(API_ACTION_STATUS.ERROR);
        setShowDeleteModal(false);
      }
    }
  }, [actionStatus]);

  // Handle delete invite action status
  useEffect(() => {
    if (actionStatus?.apiAction === HubApiActions.DELETE_CARETEAM_INVITE) {
      if (actionStatus.success) {
        setShowDeleteStatus(API_ACTION_STATUS.SUCCESS);
      } else if (actionStatus.success === false && actionStatus.error) {
        setShowDeleteStatus(API_ACTION_STATUS.ERROR);
      }
    }
  }, [actionStatus]);

  // Handle invite action status (create and resend)
  useEffect(() => {
    if (
      actionStatus?.apiAction === HubApiActions.CREATE_CARETEAM_INVITE ||
      actionStatus?.apiAction === HubApiActions.RESEND_CARETEAM_INVITE
    ) {
      if (actionStatus.success) {
        setShowInviteStatus(API_ACTION_STATUS.SUCCESS);
        setShowInviteModal(false);
      } else if (actionStatus.success === false && actionStatus.error) {
        setShowInviteStatus(API_ACTION_STATUS.ERROR);
        setShowInviteModal(false);
      }
    }
  }, [actionStatus]);

  // Handler for delete action
  const handleDeleteUser = async () => {
    if (selectedContact?.customer_id) {
      dispatch(
        deleteOwnerContact(ownerId, {
          customer_id: selectedContact.customer_id,
          status: selectedContact.status,
          email: selectedContact.email,
        }),
      );
    }
  };

  const handleStatusClose = () => {
    if (showDeleteStatus === API_ACTION_STATUS.ERROR && selectedContact) {
      // On "Try again", re-open the delete modal and clear status
      setShowDeleteStatus(null);
      setShowDeleteModal(true);
      dispatch(clearHubActionStatus());
    } else {
      // On success, close everything, clear status, and refresh contacts
      setShowDeleteStatus(null);
      setShowDeleteModal(false);
      setSelectedContact(null);
      dispatch(clearHubActionStatus());
      dispatch(fetchOwnerContacts(ownerId));
    }
  };

  const handleInviteStatusClose = () => {
    if (showInviteStatus === API_ACTION_STATUS.ERROR) {
      // On "Try again", re-open the invite modal only if it was a new invite (not resend)
      setShowInviteStatus(null);
      if (actionStatus?.apiAction === HubApiActions.CREATE_CARETEAM_INVITE) {
        setShowInviteModal(true);
      }
      dispatch(clearHubActionStatus());
    } else {
      // On success, close everything, clear status, and refresh contacts
      setShowInviteStatus(null);
      dispatch(clearHubActionStatus());
      dispatch(fetchOwnerContacts(ownerId));
    }
  };

  const getModalContent = () => {
    if (showDeleteModal && selectedContact) {
      return (
        <DeleteCareTeamUserModal
          contact={selectedContact}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedContact(null);
          }}
          onDelete={handleDeleteUser}
          isLoading={deleteContactLoading}
        />
      );
    }
    if (showDeleteStatus === API_ACTION_STATUS.SUCCESS) {
      const isInviteDelete =
        actionStatus?.apiAction === HubApiActions.DELETE_CARETEAM_INVITE;
      return (
        <StatusMessage
          icon={<DeleteSuccessIcon />}
          title={
            isInviteDelete
              ? 'Invite deleted successfully'
              : 'User deleted successfully'
          }
          message={
            isInviteDelete
              ? 'You’ll no longer see this invite in the care team.'
              : 'You’ll no longer see this user in the care team.'
          }
          onClose={handleStatusClose}
        />
      );
    }
    if (showDeleteStatus === API_ACTION_STATUS.ERROR) {
      return (
        <StatusMessage
          icon={<DeleteFailedIcon />}
          title="Deletion unsuccessful"
          message="The user didn’t delete at this time. Please try again later."
          onClose={handleStatusClose}
          primaryButtonText="Try again"
        />
      );
    }
    return null;
  };

  if (ownerContactsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          color="secondary"
          onClick={() => setShowInviteModal(true)}
          sx={{ height: '40px' }}
        >
          Invite new
        </Button>
      </Box>
      {ownerContacts?.length ? (
        <CustomerCareDTable
          rows={rows}
          columns={columns}
          pageSize={8}
          defaultSortField="role"
          defaultSortDirection="desc"
          hideViewAllButton
        />
      ) : (
        <NoTabDataCard />
      )}
      {selectedContact &&
        !showDeleteModal &&
        !deleteContactLoading &&
        !showDeleteStatus && (
          <EditRoleModal
            onClose={() => {
              setSelectedContact(null);
              dispatch(setEditStaffStatus(null));
              dispatch(clearHubActionStatus());
            }}
            contact={selectedContact}
            onSave={handleSaveRole}
            isloading={careteamRoleLoading || facilityLoading}
          />
        )}

      {showInviteModal && !showInviteStatus && (
        <InviteNewUserModal
          ownerEmail={ownerEmail}
          onClose={() => setShowInviteModal(false)}
          onInvite={(data) => {
            dispatch(
              createCareteamInvite(ownerId, {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                careteam_role_id: Number(data.role),
              }),
            );
          }}
        />
      )}

      {showInviteStatus && (
        <NewModal
          showCloseX
          maxWidth="xs"
          title="Invite new"
          onClose={() => {
            if (showInviteStatus === API_ACTION_STATUS.SUCCESS) {
              dispatch(fetchOwnerContacts(ownerId));
            }
            setShowInviteStatus(null);
            setShowInviteModal(false);
            dispatch(clearHubActionStatus());
          }}
        >
          {showInviteStatus === API_ACTION_STATUS.SUCCESS && (
            <StatusMessage
              icon={<InviteSuccessIcon />}
              title="Invite sent"
              message="Invitations will appear in the recipient's inbox."
              onClose={handleInviteStatusClose}
            />
          )}
          {showInviteStatus === API_ACTION_STATUS.ERROR && (
            <StatusMessage
              icon={<InviteFailedIcon />}
              title="Invite didn't send"
              message="Please try again later."
              onClose={handleInviteStatusClose}
              primaryButtonText="Try again"
            />
          )}
        </NewModal>
      )}

      {(showDeleteModal && selectedContact) || showDeleteStatus ? (
        <NewModal
          showCloseX
          maxWidth="xs"
          title="Delete user"
          onClose={() => {
            setShowDeleteModal(false);
            setShowDeleteStatus(null);
            setSelectedContact(null);
            dispatch(clearHubActionStatus());
          }}
        >
          {getModalContent()}
        </NewModal>
      ) : null}
    </Box>
  );
};

export default CareTeamTab;
